import isEmpty from 'lodash/isEmpty';

import {
  mapListingToProduct,
  mapSearchPageHomecardToProduct,
  mapExtraRoomsToProduct,
  mapSimilarPropertyToProduct,
  mapFavoriteToProduct,
  mapExtraRoomToCd,
  mapSimilarPropertyToCd,
  mapFavoriteToCd,
  mapBookNowButtonToECommerce,
  mapBookNowButtonToProduct
} from './parsers';

import { CustomDimensionsManager, constants } from '.';

const CHECKOUT_TRACKING_STEP = 1;
const GA_IMPRESSIONS_MAX_SIZE = 30;

export const EcommerceManagerFactory = instance => {
  const cdManager = CustomDimensionsManager(instance);
  return {
    trackSearchPageImpressions(cardList, indexedHomecards, cityId) {
      try {
        const currencyCode = isEmpty(indexedHomecards)
          ? undefined
          : indexedHomecards[Object.keys(indexedHomecards)[0]].currencyIsoCode;
        const products = Object.values(
          cardList.map((card, position) => {
            const cardData = indexedHomecards[card.id];
            return mapSearchPageHomecardToProduct(cardData, position, cityId);
          })
        );
        this.trackImpressions(products, currencyCode);
      } catch (err) {
        console.error('Failed to track search page impressions', { err });
      }
    },
    trackExtraRoomsImpressions(listings, mainListing) {
      try {
        const currencyCode = listings[0].currency.label;
        const products = listings.map((listing, position) =>
          mapExtraRoomsToProduct(listing, mainListing, position)
        );
        cdManager.clearListingCustomDimensions();
        this.trackImpressions(products, currencyCode);
      } catch (err) {
        console.error('Failed to track extra rooms impressions', { err });
      }
    },
    trackSimilarPropertiesImpressions(similarRentableUnits) {
      try {
        const currencyCode = similarRentableUnits[0].currencyIsoCode;
        const products = similarRentableUnits.map((ru, position) =>
          mapSimilarPropertyToProduct(ru, position)
        );
        cdManager.clearListingCustomDimensions();
        this.trackImpressions(products, currencyCode);
      } catch (err) {
        console.error('Failed to track similar properties impressions', {
          err
        });
      }
    },
    trackFavoritesImpressions(favorites, locale) {
      try {
        const currencyCode = favorites[0].currencyIsoCode;
        const products = favorites.map((fav, position) =>
          mapFavoriteToProduct(fav, position)
        );
        cdManager.setSearchCommonCustomDimensions({
          locale,
          pageType: constants.TRACKING_PAGE_TYPES.FAVORITES
        });
        this.trackImpressions(products, currencyCode);
      } catch (err) {
        console.error('Failed to track favorites impressions', { err });
      }
    },
    trackImpressions(products, currencyCode) {
      try {
        instance.ecommerceManager.setCurrencyCode(currencyCode);
        let chunk;
        for (let i = 0; i < products.length; i += GA_IMPRESSIONS_MAX_SIZE) {
          chunk = products.slice(i, i + GA_IMPRESSIONS_MAX_SIZE);
          instance.ecommerceManager.sendProductImpression(chunk);
        }
      } catch (e) {
        console.error('Failed to track ecommerce impressions', { e });
      }
    },
    trackSearchPageHomecardClick(homecard, position, cityId) {
      try {
        const product = mapSearchPageHomecardToProduct(
          homecard,
          position,
          cityId
        );
        const currencyCode = homecard.currencyIsoCode;
        cdManager.setSearchPropertyCustomDimensions(homecard, cityId);

        this.trackProductClick(
          product,
          currencyCode,
          constants.TRACKING_PAGE_TYPES.SEARCH_PAGE
        );
      } catch (err) {
        console.error('Failed to track search page click', { err });
      }
    },
    trackExtraRoomClick(extraRoom, mainListing, isLogged, locale, position) {
      try {
        const product = mapExtraRoomsToProduct(
          extraRoom,
          mainListing,
          position
        );
        const currencyCode = extraRoom.currency.label;
        const cdObject = mapExtraRoomToCd(
          extraRoom,
          mainListing,
          isLogged,
          locale
        );
        cdManager.setListingCustomDimensions(cdObject);

        this.trackProductClick(
          product,
          currencyCode,
          constants.LIST_E_COMMERCE.EXTRA_ROOMS
        );
      } catch (err) {
        console.error('Failed to track extra room click', { err });
      }
    },
    trackSimilarPropertyClick(listing, isLogged, locale, position) {
      try {
        const product = mapSimilarPropertyToProduct(listing, position);
        const currencyCode = listing.currencyIsoCode;
        const cdObject = mapSimilarPropertyToCd(listing, isLogged, locale);
        cdManager.setListingCustomDimensions(cdObject);

        this.trackProductClick(
          product,
          currencyCode,
          constants.LIST_E_COMMERCE.SIMILAR_PROPERTIES
        );
      } catch (err) {
        console.error('Failed to track similar properties click', { err });
      }
    },
    trackFavoriteClick(favorite, locale, position) {
      try {
        const product = mapFavoriteToProduct(favorite, position);
        const currencyCode = favorite.currencyIsoCode;
        const cdObject = mapFavoriteToCd(favorite, locale);
        cdManager.setSearchPropertyCustomDimensions(cdObject, favorite.city);

        this.trackProductClick(
          product,
          currencyCode,
          constants.TRACKING_PAGE_TYPES.FAVORITES
        );
      } catch (err) {
        console.error('Failed to track favorite click', { err });
      }
    },
    trackProductClick(product, currencyCode, list) {
      try {
        instance.ecommerceManager.setCurrencyCode(currencyCode);
        instance.ecommerceManager.sendSelectedProduct(product, {
          list
        });
      } catch (e) {
        console.error('Failed to track trackProductClick', { e });
      }
    },

    trackProductDetail({ listing, locale, isLogged, moveIn, moveOut }) {
      try {
        const product = mapListingToProduct(listing);
        cdManager.setListingCustomDimensions({
          listing,
          isLogged,
          locale,
          pageType: constants.TRACKING_PAGE_TYPES.PRODUCT_PAGE,
          moveIn,
          moveOut
        });
        instance.ecommerceManager.setCurrencyCode(
          listing.pricing.currencyLabel
        );
        instance.ecommerceManager.sendProductDetail(product);
      } catch (e) {
        console.error('Failed to track procdut detail', { e });
      }
    },

    trackCheckoutStep1(props) {
      try {
        const ecommerceObj = mapBookNowButtonToECommerce(props);
        cdManager.setBookNowButtonCustomDimensions(ecommerceObj);
        instance.ecommerceManager.setCurrencyCode(ecommerceObj.currencyCode);

        const product = mapBookNowButtonToProduct(props);

        instance.ecommerceManager.sendAddProductToCart(product);
        instance.ecommerceManager.sendCheckoutStep(product, {
          step: CHECKOUT_TRACKING_STEP
        });
      } catch (e) {
        console.error('Failed to track checkoutStep1', { e });
      }
    }
  };
};

export default {
  EcommerceManagerFactory
};
